import * as React from 'react';
import styled from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import {
    IDateParts, getDateParts, getToday, IDate, DateTime,
    sortDates, ensureDateTime, isDateValid, getDateTimeFromDateParts, getLocaleDateString, getDateFormat
} from '@agilepoint/utils-date-time';
import { IDateRangePickerProps } from './date-range-picker-types';
import { IDateInfo, CalendarView } from '../calendar';
import { getNextMonthInfo, getPrevMonthInfo } from '../calendar/utils';
import { DateRangeCalendar, DateRangePickerFooter, DateRangePickerCallout } from './date-range-picker-parts';
import { excludeKeys, KeyStrokeListener } from '../../utils';
import { HostableSurfaceContext, IHostableSurfaceContextProps } from '../hostable-surface';
import { Textbox } from '../textbox';
import { withCulture } from '../../localization/culture-provider';

interface IDateRangePickerState {
    hideCallout: boolean;
    value: DateTime[];
    intermediateValue: DateTime[];
    firstCalendarView: CalendarView;
    secondCalendarView: CalendarView;
}

function getSelectedDateView(selectedDateValues: IDateParts[], view: CalendarView) {
    if (view) {
        return view;
    } else if (selectedDateValues.length > 0) {
        return selectedDateValues[0];
    }
    return getDateParts(getToday());
}

function getDateRangeStringValue(formattedDates: string[], separator: string) {
    const [startDate, endDate = ''] = formattedDates;
    return startDate ? `${startDate} ${separator} ${endDate}` : '';
}

const getFilteredValues = (currentValue: string) => (currentValue !== undefined && currentValue !== '');

const getParsedDatesFromPropsValue = memoizeFunction((value: string, options: Pick<IDateRanngePickerBaseProps, 'separator' | 'format'>) => {
    const { separator, format } = options;
    const values = getStartAndEndDates(value, separator!);
    const filteredValues = values.filter(getFilteredValues);
    return getParsedDates(filteredValues, { format });
});

function getParsedDates(dateRangeValues: IDate[], options: Pick<IDateRanngePickerBaseProps, 'format'>) {
    const { format } = options;
    return dateRangeValues.map((selectedDate) => ensureDateTime(selectedDate, format));
}

const trim = (i: string) => i.trim();
export const getStartAndEndDates = (value: string, rangeSeperator = '') => (value ? value.split(rangeSeperator).map(trim) : []);

const defaultDateRangeView = memoizeFunction(() => {
    const todayDateParts = getDateParts(getToday());
    const firstCalendarViewInfo = {
        month: todayDateParts.month,
        year: todayDateParts.year
    };
    const secondCalendarViewInfo = getNextMonthInfo(firstCalendarViewInfo.month, firstCalendarViewInfo.year);
    return {
        firstView: firstCalendarViewInfo,
        secondView: secondCalendarViewInfo
    };
});

const dateRangeViewOnFirstSelection = memoizeFunction((selectedDateParts: IDateParts[]) => {
    const [firstView] = selectedDateParts;
    const firstCalendarViewInfo = {
        month: firstView.month,
        year: firstView.year
    };
    const secondCalendarViewInfo = getNextMonthInfo(firstCalendarViewInfo.month, firstCalendarViewInfo.year);
    return {
        firstView: firstCalendarViewInfo,
        secondView: secondCalendarViewInfo
    };
});

const Wrapper = styled.div`
    display: inline-block;
    width: 240px;
    height: 40px;
    
    ${Textbox} {
        width: 100%;
        height: 100%;
    }
`;

interface IDateRanngePickerBaseProps extends IDateRangePickerProps {
    culture?: string;
}

class DateRangePickerBase extends React.PureComponent<IDateRanngePickerBaseProps, IDateRangePickerState> {
    static contextType = HostableSurfaceContext;
    static defaultProps = {
        separator: '-'
    };

    declare context: IHostableSurfaceContextProps;
    private rootElementRef: React.RefObject<HTMLInputElement>;
    private wrapperElementRef: React.RefObject<HTMLDivElement>;
    private keyStrokeListener: KeyStrokeListener;
    static getDerivedStateFromProps(props: IDateRanngePickerBaseProps) {
        const { value, format, culture } = props;
        if (value === undefined) {
            return null;
        }
        const dateFormat = format || getDateFormat(culture);
        const parsedDateValues = value.map((date) => ensureDateTime(date, dateFormat));
        return {
            value: parsedDateValues
        };
    }

    constructor(props: IDateRanngePickerBaseProps) {
        super(props);
        const { defaultValue = [], value, view, elementRef } = this.props;
        const format = this.getDateFormat();
        const dateValue = (value ?? defaultValue).map(this.ensureDateTime);
        const sortedDates = sortDates(dateValue, format);
        const selectedDateParts = this.getDatePartsFromParsedDateValue(sortedDates as DateTime[]);

        const firstView = getSelectedDateView(selectedDateParts, view!);
        const secondView = getNextMonthInfo(firstView.month, firstView.year) as IDateParts;
        this.rootElementRef = elementRef ?? React.createRef();
        this.wrapperElementRef = React.createRef();
        this.keyStrokeListener = new KeyStrokeListener(this.rootElementRef);

        this.state = {
            hideCallout: true,
            value: dateValue,
            intermediateValue: [],
            firstCalendarView: {
                month: firstView.month,
                year: firstView.year
            },
            secondCalendarView: {
                month: secondView.month,
                year: secondView.year
            }
        };
    }

    componentDidMount() {
        const dateRangeInputValue = this.prepareDateRangeStringValue();
        this.setInputValue(dateRangeInputValue);
        this.context.addSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStrokeListener.subscribe(this.showCalendarCallout, 'Enter');
        this.keyStrokeListener.subscribe(this.closeCalenderCallout, 'Escape');
    }

    componentDidUpdate(prevProps: IDateRanngePickerBaseProps) {
        const { format, separator, value } = this.props;
        if (prevProps.format === format && prevProps.separator === separator && prevProps.value === value) {
            return;
        }
        const dateRangeInputValue = this.prepareDateRangeStringValue();
        this.setInputValue(dateRangeInputValue);
    }

    render() {
        const { hidden, ...rest } = this.props;
        const dateRangePickerProps = excludeKeys(rest, 'type', 'separator', 'defaultValue', 'format',
            'maxDate', 'minDate', 'retainPickerAfterSelection', 'value', 'elementRef',
            'view', 'weekStartDay', 'onChange', 'onViewChange', 'as');
        return hidden ? null : (
            <Wrapper ref={this.wrapperElementRef} onClick={this._openCallout}>
                <Textbox
                    {...dateRangePickerProps}
                    onBlur={this._onBlur}
                    elementRef={this.rootElementRef}
                    iconName="ap-calendar.outline"
                    iconPosition="right"
                    autoComplete="off"
                    defaultValue={this.prepareDateRangeStringValue()}
                    onValueChange={this.onInputValueChange}
                />
                {this.renderCallout()}
            </Wrapper>
        );
    }

    componentWillUnmount() {
        this.context.removeSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStrokeListener.unsubscribe();
    }

    private renderCallout = () => {
        const { disabled = false } = this.props;
        const { hideCallout } = this.state;
        return (
            <DateRangePickerCallout
                targetRef={this.wrapperElementRef}
                hidden={hideCallout || disabled}
                closeOnEscape={true}
                onDismiss={this.closeCalenderCallout}
                preventClickEventPropagation>
                {this.renderCalendar()}
                {this.renderDateRangePickerFooter()}
            </DateRangePickerCallout>
        );
    }

    private renderCalendar = () => {
        const { minDate, maxDate, weekStartDay, hideDateFromOtherMonth, showWeekNumber } = this.props;
        const { firstCalendarView, secondCalendarView } = this.state;
        const format = this.getDateFormat();
        const selectedDateValues = this.getUpdatedDateValues();
        return (
            <DateRangeCalendar
                showWeekNumber={showWeekNumber}
                hideDateFromOtherMonth={hideDateFromOtherMonth}
                onChange={this.onDateChange}
                onDateCellMouseOver={this.onDateCellMouseOver}
                onFirstCalendarViewChange={this.onFirstCalendarViewChange}
                onSecondCalendarViewChange={this.onSecondCalendarViewChange}
                value={selectedDateValues}
                format={format}
                maxDate={maxDate}
                minDate={minDate}
                weekStartDay={weekStartDay}
                firstCalendarView={firstCalendarView}
                secondCalendarView={secondCalendarView}
            />
        );
    }

    private renderDateRangePickerFooter = () => {
        const { resetButtonLabel = 'Reset', applyButtonLabel = 'Apply' } = this.props;
        return (
            <DateRangePickerFooter
                resetButtonLabel={resetButtonLabel}
                applyButtonLabel={applyButtonLabel}
                value={this.prepareDateRangeStringValue()}
                onResetClick={this.resetDateRange}
                onApplyClick={this.applyDateRange}
                separator={this.getSeparator()}
            />
        );
    }

    private getUpdatedDateValues = () => {
        const { value, intermediateValue } = this.state;
        if (intermediateValue.length > 0 && value!.length < 2) {
            return [...intermediateValue];
        }
        return [...value!];
    }

    private _openCallout = (ev: React.SyntheticEvent) => {
        ev.stopPropagation();
        this.showCalendarCallout();
    }

    private closeCalenderCallout = () => this.setState({ hideCallout: true });

    private _onBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
        const { target: { value: fieldValue } } = ev;
        const { value } = this.state;
        const separator = this.getSeparator();
        const dateRangeValues = getStartAndEndDates(fieldValue, separator);
        const isFieldValueInValid = this.isDateRangeValueInValid(dateRangeValues);
        if (isFieldValueInValid) {
            const formattedStateValues = this.formatDateRangeValues(value);
            const stateStringValue = getDateRangeStringValue(formattedStateValues, separator);
            this.setInputValue(stateStringValue);
            return;
        }
        const parsedDateValues = getParsedDates(dateRangeValues, { format: this.getDateFormat() });
        const formattedRangeDates = this.formatDateRangeValues(parsedDateValues);
        const dateRangeStringValue = getDateRangeStringValue(formattedRangeDates, separator);
        this.setInputValue(dateRangeStringValue);
    }

    private onInputValueChange = (newValue: string) => {
        const { separator } = this.props;
        const dateRangeValues = getStartAndEndDates(newValue, separator);
        if (this.isDateRangeValueInValid(dateRangeValues)) {
            return;
        }
        const parsedDateValues = getParsedDates(dateRangeValues, { format: this.getDateFormat() });
        const dateRangePickerViewObject = this.prepareCalendarViewOnChange(parsedDateValues);
        this.setState({
            value: parsedDateValues,
            firstCalendarView: dateRangePickerViewObject.firstView,
            secondCalendarView: dateRangePickerViewObject.secondView
        });

    }

    private onChange = (value: Date[]) => {
        const { onChange } = this.props;
        if (onChange === undefined) {
            return;
        }
        onChange(value);
    }

    private onDateCellMouseOver = (info: IDateInfo) => {
        const { value } = this.state;
        if (!value || value.length !== 1) {
            return;
        }
        const hoveredDateValue = getDateTimeFromDateParts(info).toJSDate();
        const [startDate] = value!;
        const newStateValue = [startDate, hoveredDateValue];
        const sortedDateValue = sortDates(newStateValue, this.getDateFormat());
        this.setState({
            intermediateValue: sortedDateValue as DateTime[]
        });

    }

    private onViewChange = (viewObject: { firstView: CalendarView, secondView: CalendarView }) => {
        this.setState({
            firstCalendarView: viewObject.firstView,
            secondCalendarView: viewObject.secondView
        });
    }

    private onFirstCalendarViewChange = (view: CalendarView) => {
        const { secondCalendarView } = this.state;

        const hasSameView = this.hasSameView(view, secondCalendarView!);

        const dateRangePickerViewObject: { firstView: CalendarView, secondView: CalendarView } = {
            firstView: view,
            secondView: hasSameView ? getNextMonthInfo(view.month, view.year) : secondCalendarView!
        };

        this.onViewChange(dateRangePickerViewObject);
    }

    private onSecondCalendarViewChange = (view: CalendarView) => {
        const { firstCalendarView } = this.state;

        const hasSameView = this.hasSameView(view, firstCalendarView!);

        const dateRangePickerViewObject: { firstView: CalendarView, secondView: CalendarView } = {
            firstView: hasSameView ? getPrevMonthInfo(view.month, view.year) : firstCalendarView!,
            secondView: view
        };

        this.onViewChange(dateRangePickerViewObject);
    }

    private onDateChange = (selectedDateValue: Date[]) => {
        const { value } = this.props;
        const formattedDates = selectedDateValue.map(this.getLocaleDateString);

        const [startDate, endDate] = formattedDates;

        const areSelectedDatesInvalid = !selectedDateValue.every(this.isDateValid);

        if (selectedDateValue.length > 0 && (areSelectedDatesInvalid || (startDate === endDate))) {
            return;
        }

        this.onChange(selectedDateValue);
        if (value === undefined) {
            this.setState({
                value: selectedDateValue.map(this.ensureDateTime),
                intermediateValue: []
            });
        }
    }

    private showCalendarCallout = () => {
        const { disabled } = this.props;
        if (disabled) {
            return;
        }
        this.setState({
            hideCallout: false
        });
    }

    private _onSurfaceClick = (ev: React.MouseEvent) => {
        const { current: rootElementRef } = this.rootElementRef;
        if (rootElementRef === null || rootElementRef.contains(ev.target as Node)) {
            return;
        }
        const fieldValue = rootElementRef.value;
        const validStateValue = this.getValidStateValueOnAction(fieldValue);
        this.setState({ ...validStateValue, hideCallout: true });
    }

    private prepareDateRangeStringValue() {
        const { value } = this.state;
        const formattedRangeDates = this.formatDateRangeValues(value);
        return getDateRangeStringValue(formattedRangeDates, this.getSeparator());
    }

    private applyDateRange = () => {
        const dateRangeInputValue = this.prepareDateRangeStringValue();
        this.setInputValue(dateRangeInputValue);
        const validStateValue = this.getValidStateValueOnAction(dateRangeInputValue);
        this.setState({ ...validStateValue, hideCallout: true });
    }

    private resetDateRange = () => {
        const { current } = this.rootElementRef;
        const fieldValue = current!.value;
        const validStateValue = this.getValidStateValueOnAction(fieldValue);
        this.setState(validStateValue);
    }

    private getValidStateValueOnAction(fieldValue: string) {
        const parsedDateValues = getParsedDatesFromPropsValue(fieldValue, { separator: this.getSeparator(), format: this.getDateFormat() });
        const dateRangePickerViewObject = this.prepareCalendarViewOnChange(parsedDateValues);
        return {
            value: parsedDateValues,
            firstCalendarView: dateRangePickerViewObject.firstView,
            secondCalendarView: dateRangePickerViewObject.secondView
        };
    }

    private setInputValue(value: string) {
        const { current } = this.rootElementRef;
        if (current === null) {
            return;
        }
        current.value = value;
    }

    private getDatePartsFromParsedDateValue(values: DateTime[]): IDateParts[] {
        return values.map(this.toDateParts);
    }

    private formatDateRangeValues(selectedDates?: DateTime[]) {
        if (selectedDates === undefined) {
            return [];
        }
        const sortedDates = sortDates(selectedDates, this.getDateFormat());
        return sortedDates.map(this.getLocaleDateString);
    }

    private toDateParts = (selectedDate: DateTime) => {
        const isInputValueValid = isDateValid(selectedDate, this.getDateFormat());
        return getDateParts(isInputValueValid ? selectedDate! : '');
    }

    private isDateRangeValueInValid = (selectedDates: string[]) => !selectedDates.every(this.isDateValid);

    private isDateValid = (date: IDate) => isDateValid(date, this.getDateFormat())

    private hasSameView = (firstView: CalendarView, secondView: CalendarView) => (firstView.year === secondView.year && firstView.month === secondView.month);

    private dateRangeViewOnSecondSelection(selectedDateParts: IDateParts[]) {
        const [firstView, secondView] = selectedDateParts;
        if (this.hasSameView(firstView, secondView)) {
            return dateRangeViewOnFirstSelection(selectedDateParts);
        }
        return {
            firstView: {
                month: firstView.month,
                year: firstView.year
            },
            secondView: {
                month: secondView.month,
                year: secondView.year
            }
        };
    }

    private prepareCalendarViewOnChange(parsedDateValues: DateTime[]) {
        const sortedDates = sortDates(parsedDateValues, this.getDateFormat());
        const sortedDateParts = this.getDatePartsFromParsedDateValue(sortedDates as DateTime[]);
        if (sortedDateParts.length === 0) {
            return defaultDateRangeView();
        } else if (sortedDateParts.length === 1) {
            return dateRangeViewOnFirstSelection(sortedDateParts);
        }
        return this.dateRangeViewOnSecondSelection(sortedDateParts);
    }

    private getSeparator() {
        const { separator } = this.props;
        return separator!;
    }

    private getDateFormat() {
        const { format, culture } = this.props;
        return format || getDateFormat(culture);
    }

    private getLocaleDateString = (date: IDate) => {
        const { culture } = this.props;
        return getLocaleDateString(date, this.getDateFormat(), culture);
    }

    private ensureDateTime = (date: IDate) => ensureDateTime(date, this.getDateFormat())
}

export const DateRangePicker = withCulture(DateRangePickerBase);
